import { defineComponent as _defineComponent } from 'vue'
import {ref, computed, onMounted, Ref} from 'vue';
import { useTranslations } from "@/hooks/useTranslations";
import { useNotify } from "@/hooks/useNotify";
import VModal from "@/components/vModal.vue";
import VModifyIngredientCost from "@/components/costing/vModifyIngredientCost.vue";
import ListRequest, { Filter, Ordering, OrderType } from "@/models/ListRequest";
import IngredientCostRepository from "@/utils/API/IngredientCostRepository";
import { getErrorMessage } from "@/utils/Helpers";
import { IngredientCost } from "@/models/IngredientCost/IngredientCost";
import VTable from "@/components/vTable.vue";
import {MeasurementCategory} from "@/models/Measurement/MeasurementCategory";
import {AxiosResponse} from "axios";
import {ListResult} from "@/utils/API/ResultTypes/ListResult";
import {Measurement} from "@/models/Measurement/Measurement";
import MeasurementRepository from "@/utils/API/MeasurementRepository";
import {IngredientCostMeasurement} from "@/models/IngredientCost/IngredientCostMeasurement";


export default /*#__PURE__*/_defineComponent({
  __name: 'vListIngredientCost',
  props: {
  showListIngredientCostModal: {
    type: Boolean,
    required: true,
  },
  ingredientCostIdsInInvoice: {
    type: Array<number>,
    required: true,
  },
},
  emits: ["on-cancel", "on-add", "on-remove", "openAddVendorModal"],
  setup(__props, { emit }) {

const props = __props;




const translations = useTranslations();
const notify = useNotify();
const loading = ref(false);
const pageSize = ref(0);
const page = ref(1);
const filter = ref<Filter[]>([
  { name: "", value: "" },
]);

const ordering = ref<Ordering>({
  field: "",
  orderType: OrderType.Asc,
});
const localIngredientCosts = ref<IngredientCost[]>([]);
const totalCount = ref(0);
const count = ref(0);
const showModifyIngredientCostModal = ref(false);
const massMeasurements = ref<IngredientCostMeasurement[]>([]);
const selectedIngredientCost : Ref<IngredientCost> = ref(null);
const searchTerm = ref("");
const tableHeaders = computed(() => [
  { name: translate("ingr-cost-table-name"), key: "Name", searchable: true, searchString: "", sortable: true },
  { name: translate("ingr-cost-table-vendor"), key: "Vendor", searchable: false, searchString: "", sortable: true },
  { name: translate("ingr-cost-table-quantity"), key: "Quantity", searchable: false, searchString: "", sortable: true },
  { name: translate("ingr-cost-table-measurement"), key: "Measurement", searchable: false, searchString: "", sortable: true },
  { name: translate("ingr-cost-table-cost"), key: "Cost", searchable: false, searchString: "", sortable: true },
  { name: translate("rec_invoice_add_new_cost"), key: "Add", searchable: false, searchString: "", sortable: true },
]);

onMounted(async() => {
  await fetchIngredientCosts();
  await loadMeasurements();
});

async function loadMeasurements() {
  let massRes: AxiosResponse<ListResult<Measurement>>;
  try {
    massRes = await MeasurementRepository.listMeasurementsForCategory(
      MeasurementCategory.Mass
    );
  }

  catch (err) {
    console.error("Failed to load measurements", err);
  }

  const mass = massRes.data.result.map((x) => ({
    id: x.id,
    weightInGrams: x.conversionCoeficient,
    ingredientCostId: 0,
    createOn: null,
    measurementId: 0,
    measurement: x,
  }));

  const volRes = await MeasurementRepository.listMeasurementsForCategory(
    MeasurementCategory.Volume
  );

  const volume = volRes.data.result
    .filter((v) => !massRes.data.result.some((m) => m.id === v.id))
    .map((x) => ({
      id: x.id,
      weightInGrams: x.conversionCoeficient,
      createOn: null,
      ingredientCostId: 0,
      measurementId: 0,
      measurement: x,
    }));

  massMeasurements.value = mass.concat(volume);
}

function translate(key: string) {
  return translations.value.get(key);
}

const hasIngredientCosts = computed(() => localIngredientCosts.value.length > 0);

function closeModal() {
  emit("on-cancel");
}

const filteredIngredientCosts = computed(() => {
  const filtered = localIngredientCosts.value.filter((x) =>
    x.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  );

  // Sort so that ingredientCostIdsInInvoice come first
  return filtered.sort((a, b) => {
    const aInInvoice = props.ingredientCostIdsInInvoice.includes(a.id);
    const bInInvoice = props.ingredientCostIdsInInvoice.includes(b.id);

    if (aInInvoice === bInInvoice) return 0;
    return aInInvoice ? -1 : 1;
  });
});

const ingredientCostsForGrid = computed(() =>
  filteredIngredientCosts.value.map((x, index) => {
    const isSelected = props.ingredientCostIdsInInvoice.includes(x.id);

    return {
      meta: { id: x.id, link: x.name },
      e1: x.vendor.name,
      e2: x.quantity,
      e3: x.ingredientCostMeasurement.measurement.name,
      e4: (Math.round(x.cost * 100) / 100).toFixed(2),
      e5: {
        component: "a",
        html: isSelected
          ? `<i class="fa fa-check-circle" style="font-size:30px; color: green"></i>`
          : `<i class="fa fa-plus-circle text-blue" style="font-size:30px;"></i>`,
        props: {
          href: "#",
          "data-target": "addIngredientCost",
          "data-toggle": "modal",
        },
        onClick: () =>
          isSelected
            ? removeIngredientCostFromInvoice(x.id)
            : addIngredientCostToInvoice(x.id),
      },
    };
  })
);

function openAddIngredientCostModal() {
  // @ts-expect-error type is close enough
  selectedIngredientCost.value = {
    id: 0,
    name: "",
    cost: 0,
    restaurantId: 0,
    recipeInvoiceId: 0,
    vendor: {
      name: "",
      restaurantId: 0,
      createdOn: null,
      id: 0
    },
    quantity: 0,
    ingredientCostMeasurementId: 0,
    vendorId: 0,
    updatedOn: null,
    createdOn: null,
    ingredientCostMeasurement: {
      measurement: {
        conversionCoeficient: 0,
        isDefault: false,
        measurementCategory: MeasurementCategory.Mass,
        name: "",
        id: 0,
      },
      weightInGrams: 0,
      measurementId: 0,
      ingredientCostId: 0,
      createdOn: null,
      id: 0
    },
    ingredientCostMeasurements: massMeasurements.value,
  };
  showModifyIngredientCostModal.value = true;
}

function hideEditIngredientCostModal(){
  showModifyIngredientCostModal.value = false;
}

function refreshIngredientCosts() {
  fetchIngredientCosts();
}

function addIngredientCostToInvoice(selectedIngredientCostId: number) {
  emit("on-add", selectedIngredientCostId);
  closeModal();
}

function removeIngredientCostFromInvoice(selectedIngredientCostId: number){
  emit("on-remove", selectedIngredientCostId);
  closeModal();
}

function onSave() {
  emit("save", localIngredientCosts.value);
}

async function fetchIngredientCosts() {
  const reqData = new ListRequest({
    page: page.value,
    pageSize: pageSize.value,
    filters: filter.value,
    orders: ordering.value ? [ordering.value] : [],
  });

  loading.value = true;
  try {
    const response = await IngredientCostRepository.listIngredientCost(reqData);
    count.value = response.data.count;
    totalCount.value = response.data.totalCount;
    localIngredientCosts.value = response.data.result;
  }
  catch (error) {
    notify.error(getErrorMessage(error, translations));
  }
  finally {
    loading.value = false;
  }
}

function changePage(args: any) {
  const [pageNum, , filtersFromTable, orderingsFromTable] = args;
  page.value = pageNum;

  if (orderingsFromTable && orderingsFromTable.length) {
    ordering.value = orderingsFromTable[0];
  }
  else {
    ordering.value = { field: "", orderType: OrderType.Asc };
  }

  filter.value = filtersFromTable && filtersFromTable.length
    ? filtersFromTable
    : [];

  fetchIngredientCosts();
}
async function order(filters: Filter[], orders: Ordering) {
  filter.value = filters;
  ordering.value = orders;
  await fetchIngredientCosts();
}

function editIngredientCost(itemId: number) {
  selectedIngredientCost.value = localIngredientCosts.value.find((x) => x.id === itemId) || null;

  // localIngredientCosts.value.forEach((row) => {
  //   row.ingredientCostMeasurements = [...massMeasurements.value];
  // });
  //
  showModifyIngredientCostModal.value = true;
}

function deleteIngredientCost(item: IngredientCost) {
  IngredientCostRepository.deleteIngredientCost(item.id)
    .then(() => {
      notify.success(translate("ingredient-cost-deleted"));
      fetchIngredientCosts();
    })
    .catch((error) => {
      notify.error(getErrorMessage(error, translations));
    });
}

return { __sfc: true,props, emit, translations, notify, loading, pageSize, page, filter, ordering, localIngredientCosts, totalCount, count, showModifyIngredientCostModal, massMeasurements, selectedIngredientCost, searchTerm, tableHeaders, loadMeasurements, translate, hasIngredientCosts, closeModal, filteredIngredientCosts, ingredientCostsForGrid, openAddIngredientCostModal, hideEditIngredientCostModal, refreshIngredientCosts, addIngredientCostToInvoice, removeIngredientCostFromInvoice, onSave, fetchIngredientCosts, changePage, order, editIngredientCost, deleteIngredientCost, VModal, VModifyIngredientCost, VTable }
}

})